import type {
    CommerceCustomer,
    CommerceCustomerChangedEvent,
    CommerceCustomerChangedListener,
    CommerceCustomerDomain,
    CommerceCustomerSession,
    CreateCommerceCustomerRequest,
    ResolveCommerceCustomerIdentityRequest,
    ResolveCommerceCustomerIdentityResponse,
    CommerceEventUnsubscribe,
    CommerceOperationOptions,
    LoginCommerceCustomerRequest,
    RequestCommerceCustomerVerificationCodeResponse,
    VerifyCommerceCustomerCodeRequest,
    CommerceCustomerRegistrationConfiguration,
    CommerceCustomerSponsor,
    CommerceCustomerAction,
} from "@teqtank/commerce-contracts";
import { DisposableListenerRegistry } from "../events/disposable-listener-registry.js";
import { CommerceError } from "../errors/commerce-error.js";
import { CommerceCustomerRepository } from "./commerce-customer-repository.js";

const ANONYMOUS_SESSION: CommerceCustomerSession = Object.freeze({
    status: "anonymous",
    customer: null,
    authenticatedAt: null,
    expiresAt: null,
    rememberMe: false,
    backOfficeUrl: null,
});

export class CommerceCustomerService implements CommerceCustomerDomain {
    private readonly listeners = new DisposableListenerRegistry<CommerceCustomerChangedEvent>();
    private current: CommerceCustomerSession = ANONYMOUS_SESSION;
    private synchronizeLogout: (() => Promise<void>) | undefined;
    public constructor(
        private readonly repository: CommerceCustomerRepository,
        private readonly assertAvailable: () => void,
    ) {}
    public get session(): CommerceCustomerSession {
        return this.current;
    }
    public get customer(): CommerceCustomer | null {
        return this.current.customer;
    }
    public get isAuthenticated(): boolean {
        return this.current.status === "authenticated";
    }
    public getActions(
        options: CommerceOperationOptions = {},
    ): Promise<readonly CommerceCustomerAction[]> {
        this.assertAvailable();
        if (!this.isAuthenticated) return Promise.resolve(Object.freeze([]));
        return this.repository.getActions(options);
    }
    public launchAction(actionId: string, options: CommerceOperationOptions = {}): Promise<string> {
        this.assertAvailable();
        if (!this.isAuthenticated)
            throw new CommerceError(
                "customer-session-refresh-failed",
                "Sign in to access account actions.",
            );
        const normalized = actionId.trim();
        if (!normalized)
            throw new CommerceError("customer-session-refresh-failed", "Select an account action.");
        return this.repository.launchAction(normalized, options);
    }
    public getRegistration(
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCustomerRegistrationConfiguration> {
        this.assertAvailable();
        return this.repository.getRegistration(options);
    }
    public searchSponsors(
        search: string,
        options: CommerceOperationOptions = {},
    ): Promise<readonly CommerceCustomerSponsor[]> {
        this.assertAvailable();
        const normalized = search.trim();
        return normalized.length < 2
            ? Promise.resolve(Object.freeze([]))
            : this.repository.searchSponsors(normalized, options);
    }
    public async resolveIdentity(
        request: ResolveCommerceCustomerIdentityRequest,
        options: CommerceOperationOptions = {},
    ): Promise<ResolveCommerceCustomerIdentityResponse> {
        this.assertAvailable();
        return this.repository.resolveIdentity(request, options);
    }
    public async create(
        request: CreateCommerceCustomerRequest,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCustomerSession> {
        this.assertAvailable();
        const session = await this.repository.create(request, options);
        return this.accept(session, "authenticated");
    }
    public async login(
        request: LoginCommerceCustomerRequest,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCustomerSession> {
        this.assertAvailable();
        const identifier = request.identifier.trim();
        if (!identifier || !request.password)
            throw new CommerceError(
                "customer-authentication-failed",
                "Username/email and password are required.",
            );
        this.accept(
            Object.freeze({ ...ANONYMOUS_SESSION, status: "authenticating" }),
            "authenticating",
        );
        try {
            return this.accept(
                await this.repository.login(
                    {
                        identifier,
                        password: request.password,
                        rememberMe: request.rememberMe ?? false,
                    },
                    options,
                ),
                "authenticated",
            );
        } catch (error) {
            this.current = ANONYMOUS_SESSION;
            throw error;
        }
    }
    public async requestVerificationCode(
        email: string,
        options: CommerceOperationOptions = {},
    ): Promise<RequestCommerceCustomerVerificationCodeResponse> {
        this.assertAvailable();
        const normalized = email.trim().toLowerCase();
        if (!normalized)
            throw new CommerceError("customer-authentication-failed", "Email is required.");
        return this.repository.requestVerificationCode(normalized, options);
    }
    public async verifyCode(
        request: VerifyCommerceCustomerCodeRequest,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCustomerSession> {
        this.assertAvailable();
        this.accept(
            Object.freeze({ ...ANONYMOUS_SESSION, status: "authenticating" }),
            "authenticating",
        );
        try {
            return this.accept(await this.repository.verifyCode(request, options), "authenticated");
        } catch (error) {
            this.current = ANONYMOUS_SESSION;
            throw error;
        }
    }
    public async logout(options: CommerceOperationOptions = {}): Promise<CommerceCustomerSession> {
        this.assertAvailable();
        const session = this.accept(
            await this.repository.logout(this.current.customer?.id, options),
            "logged-out",
        );
        await this.synchronizeLogout?.();
        return session;
    }
    public setLogoutSynchronizer(synchronize: () => Promise<void>): void {
        this.synchronizeLogout = synchronize;
    }
    public async refreshSession(
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCustomerSession> {
        this.assertAvailable();
        const session = await this.repository.refresh(this.current.customer?.id, options);
        return this.accept(session, session.status === "expired" ? "expired" : "refreshed");
    }
    public onChanged(listener: CommerceCustomerChangedListener): CommerceEventUnsubscribe {
        return this.listeners.subscribe(listener);
    }
    public dispose(): void {
        this.current = ANONYMOUS_SESSION;
        this.listeners.release();
    }
    private accept(
        session: CommerceCustomerSession,
        reason: CommerceCustomerChangedEvent["reason"],
    ): CommerceCustomerSession {
        this.current = session;
        this.listeners.emit(Object.freeze({ reason, session }));
        return session;
    }
}
